import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';
import { FaSortUp, FaSortDown, FaCheckCircle } from 'react-icons/fa';

function ModulesView() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [filters, setFilters] = useState({
    query: '',
    programId: '',
    yearId: '',
    semesterId: '',
    examType: ''
  });
  const [sortColumn, setSortColumn] = useState('moduleName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchModules();
    fetchFilters();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/modules');
      let data = Array.isArray(res.data) ? res.data : [];
      // Sort descending by id (or examId if available)
      data.sort((a, b) => (b.examId || b.id) - (a.examId || a.id));
      setModules(data);
    } catch (err) {
      setError('Error fetching modules.');
    }
    setLoading(false);
  };

  const fetchFilters = async () => {
    try {
      const [progRes, yearRes, semRes, examTypeRes] = await Promise.all([
        axios.get('/api/programs'),
        axios.get('/api/years-of-study'),
        axios.get('/api/semesters'),
        axios.get('/api/exam-types')
      ]);
      setPrograms(Array.isArray(progRes.data) ? progRes.data : (progRes.data.data || []));
      setYears(Array.isArray(yearRes.data) ? yearRes.data : (yearRes.data.data || []));
      setSemesters(Array.isArray(semRes.data) ? semRes.data : (semRes.data.data || []));
      setExamTypes(Array.isArray(examTypeRes.data) ? examTypeRes.data : (examTypeRes.data.data || []));
    } catch (err) {
      console.error('Error fetching filter data', err);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // Extract values from the module object.
  const getValue = (mod, col) => {
    switch (col) {
      case 'moduleName':
        return mod.moduleName || mod.examName || '';
      case 'moduleCode':
        return mod.moduleCode || (mod.examId ? mod.examId.toString() : '');
      case 'examType':
        return mod.examType && typeof mod.examType === 'object'
          ? mod.examType.name || ''
          : mod.examType || '';
      case 'programName':
        return mod.program && typeof mod.program === 'object'
          ? mod.program.name || ''
          : mod.program || '';
      case 'yearOfStudy':
        return mod.yearOfStudy && typeof mod.yearOfStudy === 'object'
          ? mod.yearOfStudy.name || ''
          : mod.yearOfStudy || '';
      case 'semesterName':
        return mod.semester && typeof mod.semester === 'object'
          ? mod.semester.name || ''
          : mod.semester || '';
      default:
        return '';
    }
  };

  const sortModules = (mods) => {
    return [...mods].sort((a, b) => {
      const aVal = getValue(a, sortColumn).toLowerCase();
      const bVal = getValue(b, sortColumn).toLowerCase();
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredModules = modules.filter(mod => {
    const query = filters.query.toLowerCase().trim();
    const textMatch = !query ||
      (mod.moduleName && mod.moduleName.toLowerCase().includes(query)) ||
      (mod.moduleCode && mod.moduleCode.toLowerCase().includes(query)) ||
      (mod.examType &&
        (typeof mod.examType === 'object'
          ? mod.examType.name.toLowerCase().includes(query)
          : mod.examType.toLowerCase().includes(query)));
    const programMatch =
      !filters.programId ||
      (mod.program && typeof mod.program === 'object'
        ? mod.program.id.toString() === filters.programId
        : mod.program.toString() === filters.programId);
    const yearMatch =
      !filters.yearId ||
      (mod.yearOfStudy && typeof mod.yearOfStudy === 'object'
        ? mod.yearOfStudy.id.toString() === filters.yearId
        : mod.yearOfStudy.toString() === filters.yearId);
    const semesterMatch =
      !filters.semesterId ||
      (mod.semester && typeof mod.semester === 'object'
        ? mod.semester.id.toString() === filters.semesterId
        : mod.semester.toString() === filters.semesterId);
    const examTypeMatch =
      !filters.examType ||
      (mod.examType &&
        (typeof mod.examType === 'object'
          ? mod.examType.name.toLowerCase() === filters.examType.toLowerCase()
          : mod.examType.toLowerCase() === filters.examType.toLowerCase()));
    return textMatch && programMatch && yearMatch && semesterMatch && examTypeMatch;
  });

  const sortedModules = sortModules(filteredModules);

  const handleEdit = (moduleId) => {
    navigate(`/admin/modules/edit/${moduleId}`);
  };

  // Scoring guide content itself is off-limits to Admin (Security-only) -
  // this just tells Admin at a glance whether one has been uploaded yet.
  const isScoringGuideModule = (mod) => {
    const examTypeName = mod.examType && typeof mod.examType === 'object' ? mod.examType.name : mod.examType;
    return examTypeName === 'RLP' || examTypeName === 'OSM';
  };

  const handleDelete = (moduleId, canDelete) => {
    setDeleteId(moduleId);
  };

  const confirmDeletion = async () => {
    try {
      await axios.delete(`/api/modules/${deleteId}`);
      setTooltip('Module deleted successfully!');
      fetchModules();
      setDeleteId(null);
    } catch (err) {
      setTooltip('Error deleting module.');
      setDeleteId(null);
    }
  };

  const cancelDeletion = () => {
    setDeleteId(null);
  };

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'moduleName', header: <>Module Name {sortColumn === 'moduleName' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'moduleCode', header: <>Module Code {sortColumn === 'moduleCode' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'examType', header: <>Exam Type {sortColumn === 'examType' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'programName', header: <>Program {sortColumn === 'programName' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'yearOfStudy', header: <>Year {sortColumn === 'yearOfStudy' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'semesterName', header: <>Semester {sortColumn === 'semesterName' && (sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />)}</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (mod, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td>{mod.moduleName || mod.examName || 'N/A'}</td>
      <td>{mod.moduleCode || (mod.examId ? mod.examId.toString() : 'N/A')}</td>
      <td>
        {mod.examType && typeof mod.examType === 'object'
          ? mod.examType.name
          : mod.examType || 'N/A'}
        {isScoringGuideModule(mod) && mod.hasScoringGuide && (
          <FaCheckCircle
            className="ms-1 text-success"
            title="Scoring guide uploaded"
          />
        )}
      </td>
      <td>
        {mod.program && typeof mod.program === 'object'
          ? mod.program.name
          : mod.program || 'N/A'}
      </td>
      <td>
        {mod.yearOfStudy && typeof mod.yearOfStudy === 'object'
          ? mod.yearOfStudy.name
          : mod.yearOfStudy || (mod.program?.structureType === 'MODULAR' ? 'Modular' : 'N/A')}
      </td>
      <td>
        {mod.semester && typeof mod.semester === 'object'
          ? mod.semester.name
          : mod.semester || (mod.program?.structureType === 'MODULAR' ? 'Modular' : 'N/A')}
      </td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(mod.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(mod.id, mod.canDelete)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View Modules" />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input 
          type="text" 
          name="query"
          className="form-control" 
          style={{ maxWidth: '250px' }}
          placeholder="Search by name, code, or exam type..."
          value={filters.query}
          onChange={handleFilterChange}
        />
        <select 
          name="programId"
          className="form-select" 
          style={{ maxWidth: '200px' }}
          value={filters.programId}
          onChange={handleFilterChange}
        >
          <option value="">All Programs</option>
          {programs.map(prog => (
            <option key={prog.id} value={prog.id}>{prog.name}</option>
          ))}
        </select>
        <select 
          name="yearId"
          className="form-select" 
          style={{ maxWidth: '150px' }}
          value={filters.yearId}
          onChange={handleFilterChange}
        >
          <option value="">All Years</option>
          {years.map(y => (
            <option key={y.id} value={y.id}>{y.name}</option>
          ))}
        </select>
        <select 
          name="semesterId"
          className="form-select" 
          style={{ maxWidth: '150px' }}
          value={filters.semesterId}
          onChange={handleFilterChange}
        >
          <option value="">All Semesters</option>
          {semesters.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select 
          name="examType"
          className="form-select" 
          style={{ maxWidth: '150px' }}
          value={filters.examType}
          onChange={handleFilterChange}
        >
          <option value="">All Exam Types</option>
          {examTypes.map(et => (
            <option key={et.id} value={et.name.toLowerCase()}>{et.name}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <p>Loading modules...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : sortedModules.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedModules}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {deleteId && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this module?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
    </div>
  );
}

export default ModulesView;
